import Modals from 'application/ui/modal/modal'
import { validateForm } from 'application/utils/form-validation'

/**
 * Класс для управления формой обратной связи на странице квеста.
 * Обрабатывает валидацию, отправку формы и отображение ошибок.
 */
export class FeedbackForm {
  private container: HTMLElement
  private form: HTMLFormElement
  private successMessageEl: HTMLElement | null = null
  private submitBtn: HTMLButtonElement

  /**
   * Создает экземпляр формы обратной связи.
   * @param {HTMLElement} container - HTML-элемент контейнера формы
   */
  constructor(container: HTMLElement) {
    this.container = container

    this.init()
    Modals.getInstance().addModal(
      'feedback_modal',
      {
        closeCallback: this.onModalClose
      },
      true
    )
  }

  /**
   * Инициализирует форму: находит элемент формы и подписывается на события submit и reset.
   * @private
   */
  private init() {
    this.form = this.container.querySelector('form') as HTMLFormElement
    this.submitBtn = this.form.querySelector(
      'button[type="submit"]'
    ) as HTMLButtonElement
    this.form.addEventListener('submit', this.onSubmit)
    this.form.addEventListener('reset', this.onReset)
  }

  /**
   * Обработчик отправки формы.
   * Предотвращает стандартное поведение, валидирует поля и отправляет данные на сервер.
   * @param {Event} event - Событие отправки формы
   * @private
   */
  private onSubmit = (event: Event) => {
    event.preventDefault()

    if (!validateForm(this.form)) {
      return
    }

    this.fetchForm().then((success) => {
      if (success) {
        this.form.reset()
        this.showSuccessMessage()
      }
    })
  }

  /**
   * Обработчик сброса формы.
   * Очищает все сообщения об ошибках.
   * @private
   */
  private onReset = () => {
    this.clearErrors()
  }

  /**
   * Отображает сообщение об ошибке для указанного поля.
   * @param {HTMLElement} field - HTML-элемент поля формы
   * @param {string} message - Текст сообщения об ошибке
   * @private
   */
  private showError(field: HTMLElement, message: string) {
    const errorEl = field.querySelector(':scope > [data-error]') as HTMLElement

    if (errorEl) {
      errorEl.textContent = message
    }
  }

  /**
   * Очищает все сообщения об ошибках в форме.
   * @private
   */
  private clearErrors() {
    const errorEls = this.form.querySelectorAll<HTMLElement>('[data-error]')
    errorEls.forEach((el) => (el.textContent = ''))
  }

  /**
   * Отправляет данные формы на сервер.
   * Использует FormData API для сбора данных и Fetch API для отправки.
   * @return {Promise<boolean>} Promise, который разрешается в true при успешной отправке, false при ошибке
   * @private
   */
  private async fetchForm(): Promise<boolean> {
    try {
      const formData = new FormData(this.form)
      const action = this.form.getAttribute('action')
      this.submitBtn.classList.add('loading')

      const response = await fetch(action || '', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      })

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      if (response.status !== 201) {
        throw new Error('При отправке отзыва произошла ошибка')
      }
    } catch (error) {
      this.showError(this.form, (error as Error).message)
      return false
    } finally {
      this.submitBtn.classList.remove('loading')
    }

    return true
  }

  /**
   * Обработчик закрытия модального окна.
   * Сбрасывает форму и очищает ошибки.
   * @private
   */
  private onModalClose = () => {
    this.form.reset()
    this.clearErrors()

    setTimeout(() => {
      if (this.successMessageEl) {
        this.successMessageEl.remove()
        this.successMessageEl = null
        this.container.appendChild(this.form)
      }
    }, 600)
  }

  private showSuccessMessage() {
    this.form.remove()
    this.successMessageEl = document.createElement('div')
    this.successMessageEl.className = 'quest-feedback-form__success'
    this.successMessageEl.innerHTML =
      '<p class="quest-feedback-form__success-text"><svg><use href="#check_all_big"></use></svg>Ваш отзыв успешно отправлен. Спасибо!</p><div class="quest-feedback-form__buttons"><button class="button button_view_action" type="button" data-close-modal>Закрыть</button></div>'
    this.container.appendChild(this.successMessageEl)
  }
}
